'use client';

import { useQuery } from '@tanstack/react-query';

import type { ContextSuggestions } from '../questions';
import type { ConnectChannelKey } from '../types';

const EMPTY: ContextSuggestions = { competitors: [], categories: [], audiences: [] };

/** Recommended competitors, categories and audiences for a brand. Empty while
 * loading, for a blank brand, and on any failure; every question still offers
 * "Let DANI choose" and a typed answer. */
export function useContextSuggestions(brand: string, channel: ConnectChannelKey, enabled: boolean) {
    const trimmed = brand.trim();
    const query = useQuery({
        queryKey: ['connect-context-suggestions', channel, trimmed.toLowerCase()],
        enabled: enabled && trimmed !== '',
        staleTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false,
        queryFn: async (): Promise<ContextSuggestions> => {
            const res = await fetch('/api/connect/suggestions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ kind: 'context', brand: trimmed, channel }),
            });
            if (!res.ok) return EMPTY;
            const body = (await res.json()) as Partial<ContextSuggestions>;
            return {
                competitors: body.competitors ?? [],
                categories: body.categories ?? [],
                audiences: body.audiences ?? [],
            };
        },
    });
    return { suggestions: query.data ?? EMPTY, isLoading: query.isFetching };
}
